import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Sofa, Waves, Snowflake, Phone, X, Check, MapPin } from "lucide-react";
import { API } from "@/lib/api";
import { PreviewPano } from "@/components/preview/PreviewPano";
import { ConciergeChat } from "@/components/preview/ConciergeChat";
import { toast } from "sonner";

const STYLE_OPTIONS = [
  { id: "modern", label: "Modern", icon: Sofa },
  { id: "scandinavian", label: "Scandi", icon: Snowflake },
  { id: "coastal", label: "Coastal", icon: Waves },
];

const postSignal = (slug, sessionId, signalType, payload) => {
  axios
    .post(`${API}/listings/${slug}/signals`, {
      session_id: sessionId,
      signal_type: signalType,
      payload: payload || null,
    })
    .catch(() => {});
};

export default function PreviewPage() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(false);
  const [roomIdx, setRoomIdx] = useState(0);
  const [style, setStyle] = useState(null);
  const [booking, setBooking] = useState(false);
  const sessionId = useMemo(
    () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}`),
    []
  );

  useEffect(() => {
    axios
      .get(`${API}/listings/${slug}`)
      .then((r) => setListing(r.data))
      .catch(() => setError(true));
  }, [slug]);

  const rooms = listing?.rooms || [];
  const activeRoom = rooms[roomIdx];

  // Dwell > 15s on a room => deep_interest signal
  useEffect(() => {
    if (!activeRoom) return;
    const t = setTimeout(() => postSignal(slug, sessionId, "deep_interest", activeRoom.name), 15000);
    return () => clearTimeout(t);
  }, [activeRoom, slug, sessionId]);

  const chooseStyle = (id) => {
    const next = style === id ? null : id;
    setStyle(next);
    if (next) postSignal(slug, sessionId, "style_preference", next);
  };

  const switchRoom = (i) => {
    setRoomIdx(i);
    setStyle(null);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex flex-col items-center justify-center text-center px-6" data-testid="preview-not-found">
        <h1 className="font-serif text-4xl text-ink mb-3">Listing not found</h1>
        <p className="text-ink/60 mb-6">This preview may have expired.</p>
        <a href="/" className="text-coral border-b border-coral pb-0.5">Back to Chateaux →</a>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center text-bone/50" data-testid="preview-loading">
        Loading tour…
      </div>
    );
  }

  const stagedOverlay =
    style && activeRoom?.has_staging ? listing.staging_variants?.[style] : null;

  return (
    <div className="min-h-screen bg-ink text-bone pb-24" data-testid="preview-page">
      {/* Header */}
      <header className="px-5 sm:px-8 py-4 flex items-center justify-between border-b border-bone/10">
        <a href="/" className="font-serif text-2xl tracking-tight" data-testid="preview-logo">Chateaux</a>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-coral">
          <Sparkles className="w-4 h-4" /> Interactive preview
        </div>
      </header>

      {/* Hero: 360 tour */}
      <section className="relative">
        <div className="relative w-full h-[52vh] md:h-[68vh] bg-ink overflow-hidden" data-testid="preview-hero">
          <PreviewPano panoramaUrl={activeRoom?.panorama_url} stagedUrl={stagedOverlay} />

          {/* Staging toggle */}
          {activeRoom?.has_staging && (
            <div className="absolute top-4 right-4 z-20 flex flex-col gap-2" data-testid="staging-toggle">
              {STYLE_OPTIONS.map((s) => {
                const Icon = s.icon;
                const on = style === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => chooseStyle(s.id)}
                    data-testid={`staging-style-${s.id}`}
                    className={`flex items-center gap-2 px-3 py-2 text-xs backdrop-blur-md border transition-colors ${
                      on ? "bg-coral text-bone border-coral" : "bg-ink/60 text-bone border-bone/20 hover:border-coral"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {s.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="pointer-events-none absolute bottom-4 left-4 z-20 flex items-center gap-2 bg-ink/70 backdrop-blur-md border border-bone/15 px-3 py-1.5">
            <span className="text-[11px] uppercase tracking-[0.15em] text-bone">
              {activeRoom?.name} · drag to explore
            </span>
          </div>
        </div>

        {/* Room switcher */}
        <div className="px-5 sm:px-8 py-4 flex gap-2 overflow-x-auto border-b border-bone/10" data-testid="room-switcher">
          {rooms.map((r, i) => (
            <button
              key={r.name}
              onClick={() => switchRoom(i)}
              data-testid={`room-chip-${i}`}
              className={`whitespace-nowrap px-4 py-2 text-sm border transition-colors ${
                i === roomIdx ? "bg-bone text-ink border-bone" : "text-bone/70 border-bone/20 hover:border-bone/50"
              }`}
            >
              {r.name}
            </button>
          ))}
        </div>
      </section>

      {/* Details */}
      <section className="mx-auto max-w-[900px] px-5 sm:px-8 py-10">
        <div className="flex items-center gap-2 text-coral text-sm mb-3">
          <MapPin className="w-4 h-4" />
          <span data-testid="preview-address">{listing.address}</span>
        </div>
        <div className="flex flex-wrap items-baseline gap-4 mb-6">
          <h1 className="font-serif text-4xl md:text-5xl leading-tight" data-testid="preview-price">
            {listing.price || "Price on request"}
          </h1>
        </div>
        <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="preview-description">
          {listing.description}
        </p>

        {listing.comparable_prices?.length > 0 && (
          <div className="mt-10">
            <p className="text-xs uppercase tracking-[0.2em] text-bone/50 mb-3">Nearby comparables</p>
            <div className="flex flex-wrap gap-2">
              {listing.comparable_prices.map((c, i) => (
                <span key={i} className="text-sm bg-bone/5 border border-bone/10 px-3 py-1.5 text-bone/80">
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-bone text-ink border-t border-ink/10 shadow-2xl" data-testid="preview-sticky-bar">
        <div className="mx-auto max-w-[1100px] px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {listing.agent?.photo && (
              <img src={listing.agent.photo} alt={listing.agent.name} className="w-10 h-10 rounded-full object-cover border border-ink/10" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{listing.agent?.name}</p>
              <p className="text-xs text-ink/50 truncate">{listing.agent?.phone || "Chateaux verified agent"}</p>
            </div>
          </div>
          <button
            onClick={() => {
              postSignal(slug, sessionId, "conversion", "book_showing");
              setBooking(true);
            }}
            data-testid="book-showing-btn"
            className="shrink-0 inline-flex items-center gap-2 bg-coral text-bone px-5 sm:px-7 py-3 text-sm font-medium hover:bg-ink transition-colors"
          >
            Book a showing
          </button>
        </div>
      </div>

      <ConciergeChat
        slug={slug}
        sessionId={sessionId}
        agentName={listing.agent?.name}
        onOpen={() => postSignal(slug, sessionId, "high_intent", "chat_opened")}
      />

      <ExitIntent slug={slug} sessionId={sessionId} onBook={() => setBooking(true)} />

      <AnimatePresence>
        {booking && (
          <BookingModal
            slug={slug}
            sessionId={sessionId}
            agentName={listing.agent?.name}
            onClose={() => setBooking(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

const ExitIntent = ({ slug, sessionId, onBook }) => {
  const [show, setShow] = useState(false);
  const shownRef = useRef(false);
  useEffect(() => {
    if (window.innerWidth < 768) return;
    const onLeave = (e) => {
      if (e.clientY <= 0 && !shownRef.current) {
        shownRef.current = true;
        setShow(true);
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-5"
          data-testid="exit-intent"
          onClick={() => setShow(false)}
        >
          <motion.div
            initial={{ scale: 0.94, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 10 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-bone text-ink max-w-md w-full p-8 relative"
          >
            <button onClick={() => setShow(false)} className="absolute top-4 right-4 text-ink/40" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <Phone className="w-6 h-6 text-coral mb-4" />
            <h3 className="font-serif text-3xl mb-2">Want the agent to text you?</h3>
            <p className="text-ink/60 text-sm mb-6">Drop your number and the agent will reach out about this home.</p>
            <button
              onClick={() => {
                postSignal(slug, sessionId, "conversion", "exit_intent");
                setShow(false);
                onBook();
              }}
              data-testid="exit-intent-cta"
              className="w-full bg-coral text-bone py-3.5 text-sm font-medium hover:bg-ink transition-colors"
            >
              Get a callback
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const BookingModal = ({ slug, sessionId, agentName, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        email: form.email,
        name: form.name,
        phone: form.phone,
        message: form.message,
        source: `preview-${slug}`,
      });
      postSignal(slug, sessionId, "conversion", "booking_submitted");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-5"
      data-testid="booking-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.94, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.94, y: 10 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-bone text-ink max-w-md w-full p-8 relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-ink/40" aria-label="Close" data-testid="booking-close">
          <X className="w-5 h-5" />
        </button>
        {done ? (
          <div className="text-center py-6" data-testid="booking-success">
            <div className="w-14 h-14 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-coral" />
            </div>
            <h3 className="font-serif text-3xl mb-2">You're on the list.</h3>
            <p className="text-ink/60 text-sm">{agentName || "The agent"} will reach out shortly to schedule your showing.</p>
          </div>
        ) : (
          <>
            <h3 className="font-serif text-3xl mb-2">Book a showing</h3>
            <p className="text-ink/60 text-sm mb-6">Tell {agentName || "the agent"} when works and they'll confirm.</p>
            <form onSubmit={submit} className="space-y-3">
              <input
                required placeholder="Full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="booking-name"
                className="w-full border border-ink/20 px-4 py-3 text-sm outline-none focus:border-coral bg-transparent"
              />
              <input
                required type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="booking-email"
                className="w-full border border-ink/20 px-4 py-3 text-sm outline-none focus:border-coral bg-transparent"
              />
              <input
                placeholder="Phone (optional)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="booking-phone"
                className="w-full border border-ink/20 px-4 py-3 text-sm outline-none focus:border-coral bg-transparent"
              />
              <textarea
                placeholder="Preferred times / questions (optional)" rows={2} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="booking-message"
                className="w-full border border-ink/20 px-4 py-3 text-sm outline-none focus:border-coral bg-transparent resize-none"
              />
              <button
                type="submit" disabled={busy}
                data-testid="booking-submit"
                className="w-full bg-coral text-bone py-3.5 text-sm font-medium hover:bg-ink transition-colors disabled:opacity-50"
              >
                {busy ? "Sending…" : "Request showing"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};
